'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmojiPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
  currentEmoji?: string;
}

// Emoji categories based on Figma design
const EMOJI_CATEGORIES = {
  'Work & Business': [
    '💼', '📊', '📈', '💻', '🖥️', '📱', '⚙️', '🔧', '🛠️',
    '📋', '📌', '📎', '🔍', '🎯', '💡', '🚀', '📁', '🗂️',
    '📂', '🗃️', '📇', '🗄️', '📑', '🧮', '💹', '📉', '🏢', '🏛️',
  ],
  'Health & Medical': [
    '🏥', '💊', '🩺', '🧬', '🔬', '💉', '🩹', '❤️', '🧠',
    '👁️', '🦷', '🏃', '🧘', '💪', '🍎', '🥗', '🩻', '🧪',
    '🏋️', '🚴', '🧘‍♀️', '🧘‍♂️', '🩸', '🫀', '🫁', '🦴', '🧑‍⚕️', '👨‍⚕️',
  ],
  'Shopping & Commerce': [
    '🛒', '🛍️', '💳', '💰', '🏪', '🏬', '📦', '🎁', '🏷️',
    '💵', '🛎️', '🧾', '📮', '✈️', '🚚', '🏠', '💎', '👗',
    '👠', '👜', '🛋️', '🪑', '🛏️', '🚗', '✨', '🎀', '🛍️', '💸',
  ],
  'Education & Learning': [
    '📚', '🎓', '✏️', '📝', '🎨', '🎭', '🎬', '🎮', '🎵',
    '🌍', '🔭', '🧪', '📐', '🗂️', '📰', '🖋️', '🎹', '🎸',
    '📖', '📕', '📗', '📘', '📙', '🔖', '🏫', '🧑‍🎓', '👨‍🏫', '👩‍🏫',
  ],
  'Food & Restaurant': [
    '🍕', '🍔', '🍟', '🌮', '🌯', '🍜', '🍣', '🍱', '🥗',
    '🍳', '🥘', '🍲', '🥡', '☕', '🍵', '🧁', '🎂', '🍰',
    '🍩', '🍪', '🍫', '🍬', '🍭', '🍿', '🥂', '🍷', '🍸', '🍹',
  ],
  'Travel & Places': [
    '✈️', '🚀', '🚂', '🚁', '⛵', '🚢', '🗺️', '🧭', '🏔️',
    '🏝️', '🏖️', '🌴', '🌋', '🗻', '🏕️', '⛺', '🎡', '🎢',
    '🗽', '🗼', '🏰', '🕌', '⛪', '🛕', '🌆', '🌃', '🌉', '🌁',
  ],
  'Communication': [
    '💬', '💭', '🗨️', '📞', '📲', '📧', '✉️', '📩', '📨',
    '📫', '📪', '📬', '📭', '📮', '🔔', '🔕', '📢', '📣',
    '🗣️', '👂', '👁️‍🗨️', '🔊', '🔉', '🔈', '📡', '📺', '📻', '🎙️',
  ],
  'Finance & Banking': [
    '🏦', '💰', '💵', '💴', '💶', '💷', '💸', '💳', '🧾',
    '💹', '📈', '📉', '💎', '🪙', '💲', '🏧', '📊', '🧮',
    '🔐', '🔒', '🔓', '🔑', '🗝️', '💼', '📋', '✍️', '🖊️', '🖋️',
  ],
  'Technology': [
    '🤖', '🎧', '📡', '🔌', '💾', '📀', '🖨️', '⌨️', '🖱️',
    '🔋', '📸', '🎥', '📺', '📻', '☁️', '🔐', '🛡️', '🔰',
    '💻', '🖥️', '📱', '📟', '🎮', '🕹️', '🔦', '🔭', '🔬', '🧲',
  ],
  'Design & Art': [
    '🎨', '🖌️', '🖍️', '✏️', '🖊️', '🖋️', '✒️', '📐', '📏',
    '✂️', '🧵', '🧶', '🎭', '🖼️', '🎪', '🎠', '🎡', '🎢',
    '📷', '📹', '🎬', '🎞️', '🎵', '🎶', '🎼', '🎹', '🥁', '🎷',
  ],
  'Nature & Science': [
    '🌱', '🌿', '🌲', '🌳', '🌴', '🌵', '🌻', '🌺', '🌸',
    '🌈', '☀️', '🌙', '⭐', '🌊', '🔥', '💧', '❄️', '🌪️',
    '🧬', '🔬', '🔭', '🧪', '🧫', '🧲', '⚗️', '🌡️', '🌍', '🌎',
  ],
  'Objects & Tools': [
    '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🪓', '🪚', '🔩', '⚙️',
    '🗜️', '⚖️', '🔗', '⛓️', '🧰', '🧲', '🪜', '🧯', '🛢️',
    '📦', '📫', '📪', '📬', '📭', '📮', '🗳️', '✉️', '📩', '📨',
  ],
};

export function EmojiPickerModal({
  isOpen,
  onClose,
  onSelect,
  currentEmoji = '📁',
}: EmojiPickerModalProps) {
  const [activeCategory, setActiveCategory] = React.useState<string>('Work & Business');

  if (!isOpen) return null;

  const categories = Object.keys(EMOJI_CATEGORIES);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-[720px] max-w-[95vw] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Choose Project Icon</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Selection */}
        <div className="px-6 py-4 flex items-center gap-4 border-b border-gray-100">
          <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center border border-gray-200">
            <span className="text-3xl">{currentEmoji}</span>
          </div>
          <div>
            <p className="font-medium text-gray-900">Project Icon</p>
            <p className="text-sm text-gray-500">Choose an emoji to represent your project</p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="px-6 pt-4 pb-2 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                  activeCategory === category
                    ? 'bg-brand-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Emoji Grid */}
        <div className="px-6 py-4 max-h-[320px] overflow-y-auto">
          <div className="grid grid-cols-10 gap-2">
            {EMOJI_CATEGORIES[activeCategory as keyof typeof EMOJI_CATEGORIES].map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                onClick={() => {
                  onSelect(emoji);
                  onClose();
                }}
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all',
                  'hover:bg-gray-100 hover:scale-110',
                  currentEmoji === emoji && 'bg-brand-primary/10 ring-2 ring-brand-primary'
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
